/**
 * DevicesTab — Lists devices assigned to the resident with assign/remove controls.
 *
 * Mirrors the Area Profile DevicesTab pattern:
 *   - Full table layout with Device Name, Description, Model, Serial Number, Status columns
 *   - Three-tier status: green (active), yellow (active but stale), grey (inactive)
 *   - Device links route to V2 community device pages by manufacture_id
 *
 * Actions: Assign Device button, per-row remove icon
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const STALE_THRESHOLD_MS = 30 * 3600000; // 30 hours

/* ── StatusPopover — wraps a status badge, shows detail on hover ── */
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

const DevicesTab = ({
  resDeviceData,
  residentName,
  devEditRights,
  onAssign,
  onRemove,
}) => {
  /** Build the correct profile link for a device based on manufacturer */
  const getDeviceLink = (device) => {
    const mfg = parseInt(device.manufacture_id);
    if (mfg === 4) return `/community/devices/4/${device.device_id}`;
    if (mfg === 6) return `/community/devices/6/${device.device_id}`;
    return `/community/devices/1/${device.device_id}`;
  };

  /** Derive status class and label from is_active + last_active */
  const getStatus = (device) => {
    if (!Number(device.is_active)) {
      return { className: 'dp-status--inactive', label: 'Inactive', title: undefined };
    }
    const stale = device.last_active
      ? (new Date() - new Date(device.last_active)) > STALE_THRESHOLD_MS
      : true;
    if (stale) {
      return {
        className: 'dp-status--warning',
        label: 'Active',
        title: 'Device may be stale — test the device to ensure functionality',
      };
    }
    return { className: 'dp-status--active', label: 'Active', title: undefined };
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">
          Assigned Devices
          <span className="pp-section__count">({resDeviceData.length})</span>
        </h3>
        {devEditRights ? (
          <div className="pp-section__actions">
            <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAssign}>
              + Assign Device
            </button>
          </div>
        ) : null}
      </div>
      <div className="pp-section__body pp-section__body--flush">
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                <th className="dp-th">Device Name</th>
                <th className="dp-th">Description</th>
                <th className="dp-th">Model</th>
                <th className="dp-th">Serial Number</th>
                <th className="dp-th" style={{ textAlign: 'center' }}>Status</th>
                {devEditRights ? <th className="dp-th" style={{ textAlign: 'center', width: '80px' }}>Remove</th> : null}
              </tr>
            </thead>
            <tbody>
              {resDeviceData.map((device) => {
                const status = getStatus(device);
                const linkable = [1,4,6].includes(device.manufacture_id)
                return (
                  <tr key={device.device_id} className="dp-tr">
                    <td className="dp-td">
                      {linkable
                          ? <Link to={getDeviceLink(device)} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {device.device_name || '—'}
                            </Link>
                          : device.device_name || '—'
                      }
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.descriptions || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.model_name || device.manufacture_model || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text dp-cell-text--mono">{device.manufacture_uuid || '—'}</span>
                    </td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <StatusPopover message={status.title}>
                        <span className={`dp-status ${status.className}`}>
                          <span className="dp-status__dot" aria-hidden="true" />
                          {status.label}
                        </span>
                      </StatusPopover>
                    </td>
                    {devEditRights ? (
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <button
                          className="rpp-remove-btn"
                          onClick={() => onRemove(device)}
                          title={`Remove ${device.device_name || 'device'} from ${residentName}`}
                        >
                          ✕
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {resDeviceData.length === 0 ? (
                <tr className="dp-tr">
                  <td className="dp-td" colSpan={devEditRights ? 6 : 5} style={{ textAlign: 'center' }}>
                    <span className="dp-cell-text dp-cell-text--muted">No devices assigned to this resident</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DevicesTab;
